// Logging utilities that send events to the backend.
import { DeviceInfo, getDeviceInfo } from "platform-specific/deviceinfo";
import { firebaseNewKey, getFirebaseUser, onFbUserChanged } from "internal/firebase";
import type { Datastore } from './datastore';
import { callServerApiAsync } from "system/servercall";
import { getDefaultFeatureConfig } from "feature/feature-helpers";
import { useEffect, useState } from "react";
import { undefR } from "./stdtypes";

const hourMillis = 60*60*1000;


onFbUserChanged((userId) => {
    if (userId) {
        console.log('User changed', userId.uid);
    }
});

const seenErrors = new Set<string>();

export function registerErrorCatchers() {
    console.log('registerErrorCatchers');
    if (global_error_catchers_registered) {
        console.log('Error catchers already registered');
        return; // Already registered, don't register again
    }
    global_error_catchers_registered = true;
    
    if (process.env.NODE_ENV !== 'test') {
        window.addEventListener('error', async (event) => {
            if (global_in_error_handler) {
                return; // avoid infinite error loop if this fails
            }
            const errorKey = event.error?.message || event.message;
            if (seenErrors.has(errorKey)) {
                console.log('Already logged this error', errorKey);
                return; // Already logged this error
            }
            seenErrors.add(errorKey);
            global_in_error_handler = true;
            console.log('Caught an error', {error: event.error});
            logEventAsync(null, 'error', {
                name: event.error?.name || "Error",
                message: event.error?.message || event.message, 
                url: window.location.href,
                stack: event.error?.stack || null
            });
            global_in_error_handler = false;
        })
    }

    window.addEventListener('unhandledrejection', async event => {
        if (global_in_error_handler) {
            return; // avoid infinite error loop if this fails
        }
        global_in_error_handler = true;
        const error = event.reason;
        console.log('Caught an unhandled promise rejection', {error});
        await logEventAsync(null, 'error', {
            name: error?.name || "Error",
            message: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : null,
            url: window.location.href,
        });
        global_in_error_handler = false;
    })
}

export type LogErrorArgs = {
    datastore: Datastore | null;
    error: any;
    props?: Record<string, any>;
};

export function logError({datastore, error, props}: LogErrorArgs) {
    logEventAsync(datastore, 'error', {
        name: error?.name || "Error",
        message: error?.message, 
        url: window.location.href,
        stack: error?.stack, ...props});
}

let global_last_event = null;
let global_in_error_handler = false;
let global_error_catchers_registered = false;


let globalSessionKey : string | null = null;
let globalSessionTime : number | null = null;
let globalDeviceInfo : DeviceInfo | null = null;

function isKnownBrowserName(name: string) {
    const known = ['Chrome', 'Firefox', 'Safari', 'Internet Explorer', 'Edge', 'Opera', 'React Native'];
    return known.includes(name);
}

function shouldFilterError({stack, deviceInfo}: {stack?: string | null, deviceInfo: DeviceInfo | null}) {
    if (stack) {
        const lower = stack.toLowerCase();
        const blocked = ['chrome-extension:', 'webkit-masked-url', 'duckduckgo-privacy-protection', 'anonymous code'];
        if (blocked.some(p => lower.includes(p))) {
            return true;
        }
    }
    if (deviceInfo && !isKnownBrowserName(deviceInfo.browserName)) {
        return true;
    }
    return false;
}

function getEnabledFeatures(datastore: Datastore | null) {
    const structureKey = datastore?.getStructureKey();
    const turnedOnFeatures = datastore?.getGlobalProperty('features') ?? {};
    const defaultFeatures = structureKey ? getDefaultFeatureConfig()[structureKey] ?? {} : {};
    return {...defaultFeatures, ...turnedOnFeatures};
}

export async function logEventAsync(datastore: Datastore | null, eventType, params=undefR) {
    try {
        let isNewSession = false;
        const preFirebaseUser = getFirebaseUser();
        const siloKey = datastore?.getSiloKey();
        const structureKey = datastore?.getStructureKey() || null;
        const instanceKey = datastore?.getInstanceKey() || null;
        const roles = datastore?.sessionData?.roles;
        const isAdmin = roles && roles.length > 0;
        const enabledFeatures = getEnabledFeatures(datastore);
        const url = window.location.href;

        if (!globalSessionKey || !globalSessionTime || Date.now() - globalSessionTime > hourMillis){
            globalSessionKey = firebaseNewKey();
            globalSessionTime = Date.now();
            globalDeviceInfo = getDeviceInfo();
            isNewSession = true;
            // console.log('New session', globalSessionKey, globalDeviceInfo);
        }

        if (eventType === 'error' && shouldFilterError({stack: params?.stack || null, deviceInfo: globalDeviceInfo})) {
            return;
        }

        // IMPORTANT: We can't use datastore.callServerAsync here because datastore will
        // be null if we are called from an unhandled promise rejection
        const result = await callServerApiAsync({datastore, component: 'eventlog', funcname: 'logEvent', params: {
            eventType, sessionKey: globalSessionKey, structureKey, instanceKey, isNewSession, params, deviceInfo: globalDeviceInfo, siloKey, 
            isAdmin, enabledFeatures, url
        }});
        global_last_event = result?.eventKey ?? null;

        const postFirebaseUser = getFirebaseUser();

    } catch (e) {
        // Exceptions during logging need to be swallowed, otherwise we'll get stuck in an infinite loop
        console.error('Error logging event', e);
    }
}

export function useStartTime(){
    const [startTime, setStartTime] = useState(Date.now());
    useEffect(() => {
        setStartTime(Date.now());
    }, []);
    return startTime;
}
